import React, { useMemo } from "react"
import useAppDispatch from "hooks/useAppDispatch"
import { Card } from "antd-v5"
import { GetScheduleFromTableInterface } from "@type/schedules.types"
import AntdModal from "components/AntdModal"
import { WarningOutlined, CalendarOutlined } from "@ant-design/icons"
import moment from "moment"
import { AntdButton } from "components"
import ShiftsStatsCard from "./ShiftsStatsCard"
import ShiftTile from "./ShiftTile"
import { publishSchedule, selectScheduleSaving } from "store/Schedules/schedules.slice"
import { DATE_FORMATS, ShiftPublicationStatusEnum } from "config/constants"
import useAppSelector from "hooks/useAppSelector"
import { momentZ } from "config/moment"

interface Props {
	open: boolean
	onClose: () => void
	shifts: (GetScheduleFromTableInterface | null)[]
}

const PublishScheduleModal: React.FC<Props> = ({ open, onClose, shifts }) => {
	const dispatch = useAppDispatch()
	const scheduleSaving = useAppSelector(selectScheduleSaving())
	const validShifts = useMemo(
		() =>
			shifts.filter(
				shift => shift?.publicationStatus === ShiftPublicationStatusEnum.UNPUBLISHED,
			) as GetScheduleFromTableInterface[],
		[shifts],
	)

	const { totalHours, assigned, unassigned, groupedShifts } = useMemo(() => {
		const { hours, assignedCount, unassignedCount, groups } = validShifts.reduce(
			(acc, shift) => {
				// Hours
				const start = moment(shift?.startTime)
				const end = moment(shift?.finishTime)
				acc.hours += end.diff(start, "hours", true)

				// Assigned / Unassigned
				if (shift?.employee) acc.assignedCount++
				else acc.unassignedCount++

				// Group by Date
				//const dateKey = momentZ(shift.date, DATE_FORMATS.DD_MM_YYYY).format(DATE_FORMATS.DD_MM_YYYY)
				const dateKey = shift.date
				if (!acc.groups[dateKey]) acc.groups[dateKey] = []
				acc.groups[dateKey].push(shift)

				return acc
			},
			{
				hours: 0,
				assignedCount: 0,
				unassignedCount: 0,
				groups: {} as Record<string, GetScheduleFromTableInterface[]>,
			},
		)

		// sort grouped keys by date and sort shifts within each date by start time
		const sortedGroups: Record<string, GetScheduleFromTableInterface[]> = {}

		Object.keys(groups)
			.sort((a, b) => moment(a).diff(moment(b)))
			.forEach(key => {
				sortedGroups[key] = groups[key].sort((a, b) => moment(a.startTime).diff(moment(b.startTime)))
			})

		return {
			totalHours: Math.round(hours),
			assigned: assignedCount,
			unassigned: unassignedCount,
			groupedShifts: sortedGroups,
		}
	}, [validShifts])

	const handleSubmit = () => {
		dispatch(
			publishSchedule(
				validShifts.map(shift => shift._id),
				() => {
					onClose()
				},
			),
		)
	}

	const modalFooter = (
		<div className="mt-2 flex w-full items-center justify-between pt-4">
			<div className="text-sm text-gray-700">
				Publishing <span className="font-bold">{validShifts?.length}</span> shifts to the roaster
			</div>
			<div className="flex gap-2">
				<AntdButton onClick={onClose} size="large" className="border-gray-300 bg-white text-gray-700">
					Cancel
				</AntdButton>
				<AntdButton
					loading={scheduleSaving}
					onClick={handleSubmit}
					size="large"
					type="primary"
					className="bg-green-600 hover:bg-green-700"
					style={{ backgroundColor: "#10b981", borderColor: "#10b981" }}
				>
					Publish
				</AntdButton>
			</div>
		</div>
	)

	return (
		<AntdModal
			open={open}
			onCancel={onClose}
			title={null}
			footer={modalFooter}
			width={600}
			closable={false}
			childrenClassName="!overflow-hidden flex flex-col"
		>
			<div className="flex-shrink-0">
				<h2 className="text-xl font-semibold text-gray-900">Review & Publish Shifts</h2>
				<p className="mt-1 mb-6 text-sm text-gray-500">
					Review selected shifts before publishing to the roster
				</p>

				<div className="mb-6 flex gap-3">
					<ShiftsStatsCard title="Total Shifts" number={validShifts?.length} />
					<ShiftsStatsCard title="Total Hours" number={totalHours} />
					<ShiftsStatsCard title="Assigned" number={assigned} textColor="text-assigned" />
					<ShiftsStatsCard title="Unassigned" number={unassigned} textColor="text-unassigned" />
				</div>

				{unassigned > 0 && (
					<div className="mb-6 flex items-start rounded-lg border border-orange-200 bg-orange-50 p-4">
						<WarningOutlined className="mt-0.5 mr-3 text-orange-500" />
						<div>
							<h4 className="text-sm font-medium text-orange-700">Unassigned Shifts Detected</h4>
							<p className="mt-1 text-sm text-orange-600">
								You have {unassigned} unassigned shifts. These shifts will be published but not assigned
								to any employee.
							</p>
						</div>
					</div>
				)}
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto rounded-lg pb-2">
				{Object.entries(groupedShifts).map(([date, shifts]) => (
					<Card
						key={date}
						className="mb-2 border border-gray-200 bg-gray-50"
						bodyStyle={{ padding: "0" }}
						title={
							<div className="flex w-full items-center justify-between font-normal">
								<span className="flex items-center text-[15px] font-semibold text-gray-800">
									<CalendarOutlined className="mr-2 text-gray-500" />
									{momentZ(date, DATE_FORMATS.DD_MM_YYYY).format(DATE_FORMATS.dddd_MM_D_YYYY)}
								</span>
								<span className="text-sm font-normal text-gray-500">
									{shifts.length} shift{shifts.length > 1 ? "s" : ""}
								</span>
							</div>
						}
					>
						<div className="flex w-full flex-col">
							{shifts?.map(shift => {
								const assigned = !!shift?.employee
								const tileColor = assigned ? "bg-assigned" : "bg-unassigned" // Green for assigned, Orange for unassigned
								const employeeName = shift?.employeeName || shift?.name || "Unallocated Shift"
								const postName = shift.post?.name || ""

								return (
									<ShiftTile
										key={shift?._id}
										employeeName={employeeName}
										postName={postName}
										startTime={shift?.startTime}
										finishTime={shift?.finishTime}
										color={tileColor}
									/>
								)
							})}
						</div>
					</Card>
				))}
				{Object.keys(groupedShifts).length === 0 && (
					<div className="py-4 text-center text-gray-500">
						No active unpublished shifts available for publishing.
					</div>
				)}
			</div>
		</AntdModal>
	)
}

export default PublishScheduleModal
